import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    FiCheck,
    FiX,
    FiAlertTriangle,
    FiInfo
} from 'react-icons/fi';
import Button from '../../../components/Button/Button';
import DynamicIconRenderer from '../../../components/DynamicIconRenderer/DynamicIconRenderer';
import { _getPublicStakingProducts, _createStakingSubscription, _getUserBalances, _getStakingEstimatedReward } from '../redux/actions';
import { showErrorSnackbar, showSuccessSnackbar } from '../../snackbar/helpers';
import Styles from './stakingSingleCoinPage.module.css';

const StakingSingleCoinPage = () => {
    const { coin } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { stakingLoading, userData } = useSelector(state => ({
        stakingLoading: state.staking.loading,
        userData: state.userData?.value || {}
    }));

    // We'll fetch balances directly instead of using Redux

    // Use a single state object to manage all component state
    const [state, setState] = useState({
        selectedPlan: null,
        stakeAmount: '',
        availableBalance: 0,
        stakable: false, // Track if the coin is stakable
        errorMessage: '',
        showConfirmation: false,
        agreeToTerms: false,
        coinData: null,
        stakingPlans: [],
        estimatedEarnings: null, // New field for API earnings
        earningsLoading: false, // Loading state for earnings API
        isRedirecting: false // Add redirecting state for 3-second delay
    });

    // Create updater functions that use the functional update pattern
    // IMPORTANT: Always use this function for partial state updates
    // For complete state updates (like after API calls), use setState directly
    const updateState = useCallback((updates) => {
        // console.log("Updating state with:", updates); // Commented out to prevent excessive logging
        setState(prevState => ({ ...prevState, ...updates }));
    }, []);

    // Get coin data and staking plans
    useEffect(() => {
        console.log('StakingSingleCoinPage - coin param:', coin);

        // Fetch specific staking pool data using currency_id
        const fetchPoolData = () => {
            const currencyId = coin.toLowerCase();
            console.log('Fetching staking pool data for currency_id:', currencyId);

            _getPublicStakingProducts(
                { currencyId },
                (response) => {
                    console.log('Pool data response:', response);
                    const data = Array.isArray(response.data) ? response.data : [response.data];

                    if (data && data.length > 0) {
                        const poolData = data[0];
                        const asset = poolData.asset || coin.toUpperCase();

                        // Create staking plans from the API data
                        const plans = [];

                        // Process period_summary_list to create plans
                        if (poolData.period_summary_list && poolData.period_summary_list.length > 0) {
                            // Sort periods by duration (0 first, then ascending)
                            const sortedPeriods = [...poolData.period_summary_list].sort((a, b) =>
                                parseInt(a.duration) - parseInt(b.duration)
                            );

                            sortedPeriods.forEach(period => {
                                const durationDays = parseInt(period.duration) || 0;
                                const durationType = period.type || (durationDays === 0 ? 'flexible' : 'fixed'); // Use API type field
                                const durationText = durationType === 'flexible' ? 'Flexible' : `${durationDays} Days`;

                                // Convert APR from decimal to percentage (e.g., 0.15 -> 15%)
                                // API returns APR as percentage (e.g., 40.0 = 40%), so no need to multiply by 100
                                const apr = parseFloat(period.apr || 0);

                                // Get min/max amounts with proper fallbacks
                                const minStake = parseFloat(period.min_amount) || 0.001;
                                const maxStake = parseFloat(period.max_amount) || 10;

                                // Check if the product is sold out
                                const soldOut = period.sold_out === true;

                                // Get product name
                                const productName = period.product_name || `${asset.toLowerCase()}_${durationDays}`;

                                // Use risk from API if available, otherwise determine based on APR
                                let risk = period.risk || 'Low';
                                if (!period.risk) {
                                    if (apr > 15) risk = 'High';
                                    else if (apr > 5) risk = 'Medium';
                                    else risk = 'Low';
                                }

                                // Use reward from API if available
                                const reward = period.reward || (durationType === 'flexible' ? 'Daily' : 'At Maturity');

                                // Use is_popular from API only (no fallbacks)
                                const popular = period.is_popular === true;

                                // Determine rewards frequency based on duration
                                // Use daily_apr if available to determine if rewards are daily
                                const hasDaily = period.daily_apr !== undefined;
                                const rewards = hasDaily || durationDays === 0 || durationDays <= 30 ? 'Daily' : 'Weekly';

                                // Use features from API, don't generate fallback features
                                const features = period.features || [];

                                plans.push({
                                    id: `${asset.toLowerCase()}-${durationDays || 'flexible'}`,
                                    periodId: period.period_id,
                                    type: durationType,
                                    duration: durationText,
                                    durationDays,
                                    apr,
                                    minStake,
                                    maxStake,
                                    rewards: reward, // Use reward from API
                                    risk,
                                    popular,
                                    soldOut,
                                    productName,
                                    dailyApr: period.daily_apr,
                                    features, // Use features from API directly
                                    iconUrl: poolData.icon_url,
                                    // New fields from API
                                    minAmount: parseFloat(period.min_amount || 0),
                                    maxAmount: parseFloat(period.max_amount || 0),
                                    apiType: period.type, // Store original API type
                                    rawPeriodData: period // Store full period data for reference
                                });
                            });
                        }

                        // If no plans were created from the API, use default plans
                        if (plans.length === 0) {
                            console.warn('No staking periods found in API response, using default plans');
                            plans.push(
                                {
                                    id: `${asset.toLowerCase()}-flexible`,
                                    type: 'Flexible',
                                    duration: 'Flexible',
                                    durationDays: 0,
                                    apy: 6.5,
                                    minStake: 0.001,
                                    maxStake: 10,
                                    rewards: 'Daily',
                                    risk: 'Low',
                                    popular: false,
                                    features: ['Withdraw anytime', 'No lock-up period', 'Daily rewards', 'Lower APY']
                                },
                                {
                                    id: `${asset.toLowerCase()}-60`,
                                    type: 'Locked',
                                    duration: '60 Days',
                                    durationDays: 60,
                                    apy: 10.2,
                                    minStake: 0.001,
                                    maxStake: 10,
                                    rewards: 'Weekly',
                                    risk: 'Medium',
                                    popular: false,
                                    features: ['60-day commitment', 'Competitive APY', 'Weekly rewards', 'Most popular choice']
                                }
                            );
                        }

                        // Sort plans by duration (flexible first, then ascending by days)
                        plans.sort((a, b) => a.durationDays - b.durationDays);

                        // Find the most popular plan or default to the middle one
                        const popularPlan = plans.find(plan => plan.popular) || plans[Math.floor(plans.length / 2)];

                        // Update all state at once to avoid render loops
                        const selectedPlanId = plans.length > 0 && popularPlan ? popularPlan.id : null;

                        // Create the state update object
                        const stateUpdate = {
                            coinData: {
                                symbol: asset,
                                asset,
                                coin: getCoinName(asset),
                                color: getDefaultColor(asset),
                                iconUrl: poolData.icon_url,
                                rawData: poolData
                            },
                            stakingPlans: plans,
                            selectedPlan: selectedPlanId
                        };

                        // Update the state in a single call
                        setState(prevState => ({ ...prevState, ...stateUpdate }));
                    } else {
                        console.log('No pool data found for:', currencyId);
                        // Fallback to using the existing stakingPools data
                        fallbackToExistingData();
                    }
                },
                (error) => {
                    console.error('Error fetching pool data:', error);
                    // Fallback to using the existing stakingPools data
                    fallbackToExistingData();
                }
            );
        };

        // Fallback function to create default staking plans if API call fails
        const fallbackToExistingData = () => {
            // Create default staking plans using the coin from URL
            const asset = coin.toUpperCase();

            // Create default staking plans
            const plans = [
                {
                    id: `${asset.toLowerCase()}-flexible`,
                    type: 'Flexible',
                    duration: 'Flexible',
                    durationDays: 0,
                    apr: 6.5,
                    minStake: 0.001,
                    maxStake: 10,
                    rewards: 'Daily',
                    risk: 'Low',
                    popular: false,
                    features: ['Withdraw anytime', 'No lock-up period', 'Daily rewards', 'Lower APR']
                },
                {
                    id: `${asset.toLowerCase()}-30`,
                    type: 'Locked',
                    duration: '30 Days',
                    durationDays: 30,
                    apr: 8.5,
                    minStake: 0.001,
                    maxStake: 10,
                    rewards: 'Daily',
                    risk: 'Low',
                    popular: false,
                    features: ['30-day commitment', 'Higher APR', 'Daily rewards', 'Auto-renewal option']
                },
                {
                    id: `${asset.toLowerCase()}-60`,
                    type: 'Locked',
                    duration: '60 Days',
                    durationDays: 60,
                    apr: 10.2,
                    minStake: 0.001,
                    maxStake: 10,
                    rewards: 'Weekly',
                    risk: 'Medium',
                    popular: false,
                    features: ['60-day commitment', 'Competitive APR', 'Weekly rewards', 'Most popular choice']
                },
                {
                    id: `${asset.toLowerCase()}-90`,
                    type: 'Locked',
                    duration: '90 Days',
                    durationDays: 90,
                    apr: 12.8,
                    minStake: 0.001,
                    maxStake: 10,
                    rewards: 'Weekly',
                    risk: 'Medium',
                    popular: false,
                    features: ['90-day commitment', 'Highest APR', 'Weekly rewards', 'Maximum returns']
                }
            ];

            // Update all state at once
            // Create the state update object
            const stateUpdate = {
                coinData: {
                    symbol: asset,
                    asset,
                    coin: getCoinName(asset),
                    color: getDefaultColor(asset)
                },
                stakingPlans: plans,
                selectedPlan: `${asset.toLowerCase()}-60` // Default to 60-day plan
            };

            // Update the state in a single call
            setState(prevState => ({ ...prevState, ...stateUpdate }));
        };

        // Start the fetch process
        fetchPoolData();
    }, [coin]); // Only depend on coin, not stakingPools

    // Fetch user balances directly from API
    useEffect(() => {
        if (!coin || !userData.email) {
            updateState({ availableBalance: 0 });
            return;
        }

        const currentCoin = coin.toLowerCase();

        // Fetch user balances directly from the API
        const fetchUserBalances = () => {
            _getUserBalances(
                (balancesData) => {
                    // Find the balance for the current coin
                    const balance = balancesData.find(b => b.currency?.toLowerCase() === currentCoin);

                    if (balance) {
                        updateState({
                            availableBalance: parseFloat(balance.balance) || 0,
                            stakable: balance.stakable || false
                        });
                    } else {
                        updateState({
                            availableBalance: 0,
                            stakable: false
                        });
                    }
                },
                (error) => {
                    console.error('Error fetching user balances:', error);
                    updateState({ availableBalance: 0 });
                }
            );
        };

        // Only fetch balances if user is logged in
        if (userData.email) {
            fetchUserBalances();
        }
    }, [coin, userData.email, updateState]);

    // Fetch estimated earnings when amount or plan changes
    const fetchEstimatedEarnings = useCallback((amount, planData) => {
        if (!amount || !planData || !planData.periodId || parseFloat(amount) <= 0) {
            updateState({ estimatedEarnings: null, earningsLoading: false });
            return;
        }

        updateState({ earningsLoading: true });

        const params = {
            amount: parseFloat(amount),
            staking_period_id: planData.periodId
        };

        _getStakingEstimatedReward(
            params,
            (response) => {
                console.log('Estimated earnings response:', response);
                updateState({
                    estimatedEarnings: response,
                    earningsLoading: false
                });
            },
            (error) => {
                console.error('Error fetching estimated earnings:', error);
                updateState({
                    estimatedEarnings: null,
                    earningsLoading: false
                });
            }
        );
    }, [updateState]);

    // Trigger estimated earnings fetch when amount or selected plan changes
    useEffect(() => {
        const selectedPlanData = state.stakingPlans.find(plan => plan.id === state.selectedPlan);

        if (state.stakeAmount && selectedPlanData) {
            // Debounce the API call to avoid too many requests
            const timeoutId = setTimeout(() => {
                fetchEstimatedEarnings(state.stakeAmount, selectedPlanData);
            }, 500); // 500ms debounce

            return () => clearTimeout(timeoutId);
        } else {
            updateState({ estimatedEarnings: null, earningsLoading: false });
        }
    }, [state.stakeAmount, state.selectedPlan, state.stakingPlans, fetchEstimatedEarnings, updateState]);

    // Cleanup effect to restore body scroll when component unmounts
    useEffect(() => {
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const formatNumber = (num, decimals = 6) => {
        if (!num || isNaN(num)) return '0.000000';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    };

    // Get full coin name from asset symbol
    const getCoinName = (asset) => {
        const coinNames = {
            'BTC': 'Bitcoin',
            'ETH': 'Ethereum',
            'SOL': 'Solana',
            'TRX': 'TRON',
            'FLR': 'Flare',
            'ADA': 'Cardano',
            'DOT': 'Polkadot',
            'USDT': 'Tether USD',
            'USDC': 'USD Coin',
            'BNB': 'Binance Coin',
            'MATIC': 'Polygon',
            'LINK': 'Chainlink',
            'AVAX': 'Avalanche'
        };
        return coinNames[asset] || asset;
    };

    const getRiskColor = (risk) => {
        switch (risk?.toLowerCase()) {
            case 'low': return '#10b981';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            default: return '#8b9aab';
        }
    };

    const getDefaultIcon = (symbol) => {
        if (!symbol) return '?'; // Return a fallback if symbol is undefined or null

        const icons = {
            'BTC': '₿',
            'ETH': 'Ξ',
            'ADA': '₳',
            'SOL': '◎',
            'DOT': '●',
            'USDT': '₮',
            'LINK': '🔗',
            'AVAX': '🔺'
        };
        return icons[symbol] || (typeof symbol === 'string' ? symbol.charAt(0) : '?');
    };

    const getDefaultColor = (symbol) => {
        if (!symbol) return '#009999'; // Return a default color if symbol is undefined or null

        const colors = {
            'BTC': '#f7931a',
            'ETH': '#627eea',
            'ADA': '#0033ad',
            'SOL': '#9945ff',
            'DOT': '#e6007a',
            'USDT': '#26a17b',
            'LINK': '#375bd2',
            'AVAX': '#e84142'
        };
        return colors[symbol] || '#009999';
    };

    // Helper function to get feature icons dynamically
    const getFeatureIcon = (feature) => {
        // Handle both string features and object features with icon property
        const featureName = typeof feature === 'string' ? feature : feature?.name || '';
        const featureIcon = typeof feature === 'object' ? feature?.icon : null;

        // Clean up malformed icon names (remove JSX syntax)
        const cleanIconName = featureIcon ? featureIcon.replace(/[<>/\s]/g, '') : null;

        // If feature has an explicit icon property, use dynamic renderer
        if (cleanIconName) {
            return (
                <DynamicIconRenderer
                    iconName={cleanIconName}
                    size={16}
                    fallback={<FiCheck size={16} />}
                />
            );
        }

        // Fallback to feature name-based mapping using dynamic renderer
        const lowerFeatureName = featureName.toLowerCase();
        let fallbackIconName = 'FiCheck'; // Default

        if (lowerFeatureName.includes('withdraw') || lowerFeatureName.includes('flexible')) {
            fallbackIconName = 'FiUnlock';
        } else if (lowerFeatureName.includes('lock') || lowerFeatureName.includes('commitment')) {
            fallbackIconName = 'FiLock';
        } else if (lowerFeatureName.includes('daily') || lowerFeatureName.includes('reward')) {
            fallbackIconName = 'FiRefreshCw';
        } else if (lowerFeatureName.includes('apy') || lowerFeatureName.includes('apr') || lowerFeatureName.includes('yield')) {
            fallbackIconName = 'FiTrendingUp';
        } else if (lowerFeatureName.includes('popular') || lowerFeatureName.includes('choice')) {
            fallbackIconName = 'FiStar';
        } else if (lowerFeatureName.includes('secure') || lowerFeatureName.includes('safe')) {
            fallbackIconName = 'FiShield';
        } else if (lowerFeatureName.includes('fast') || lowerFeatureName.includes('quick')) {
            fallbackIconName = 'FiZap';
        } else if (lowerFeatureName.includes('time') || lowerFeatureName.includes('period')) {
            fallbackIconName = 'FiClock';
        } else if (lowerFeatureName.includes('auto') || lowerFeatureName.includes('renewal')) {
            fallbackIconName = 'FiRefreshCw';
        }

        return (
            <DynamicIconRenderer
                iconName={fallbackIconName}
                size={16}
                fallback={<FiCheck size={16} />}
            />
        );
    };

    const selectedPlanData = useMemo(() => {
        // Make sure stakingPlans is an array before trying to find
        if (!Array.isArray(state.stakingPlans)) {
            return null;
        }

        return state.stakingPlans.find(plan => plan.id === state.selectedPlan);
    }, [state.stakingPlans, state.selectedPlan]);

    const validateStakeAmount = () => {
        // If we don't have selectedPlanData yet, don't validate
        if (!selectedPlanData || !state.coinData) {
            return false;
        }

        const amount = parseFloat(state.stakeAmount);

        // Basic validation
        if (!state.stakeAmount || isNaN(amount)) {
            updateState({ errorMessage: 'Please enter a valid amount' });
            return false;
        }

        if (amount <= 0) {
            updateState({ errorMessage: 'Amount must be greater than 0' });
            return false;
        }

        // Check if user is logged in
        if (!userData.email) {
            updateState({ errorMessage: 'Please log in to stake' });
            return false;
        }

        // Check if user has sufficient balance
        if (amount > state.availableBalance) {
            updateState({ errorMessage: `Insufficient balance. Available: ${formatNumber(state.availableBalance)} ${state.coinData.asset}` });
            return false;
        }

        // Check plan limits
        if (amount < selectedPlanData.minStake) {
            updateState({ errorMessage: `Minimum stake amount is ${formatNumber(selectedPlanData.minStake)} ${state.coinData.asset}` });
            return false;
        }

        if (amount > selectedPlanData.maxStake) {
            updateState({ errorMessage: `Maximum stake amount is ${formatNumber(selectedPlanData.maxStake)} ${state.coinData.asset}` });
            return false;
        }

        // Clear any error message if validation passes
        updateState({ errorMessage: '' });
        return true;
    };

    // Memoize the validation result to prevent infinite loops
    const isValidStakeAmount = useMemo(() => {
        // Basic validation without updating state
        if (!selectedPlanData || !state.coinData) {
            return false;
        }

        const amount = parseFloat(state.stakeAmount);

        // Check all validation conditions without updating state
        if (!state.stakeAmount || isNaN(amount) || amount <= 0) {
            return false;
        }

        if (!userData.email) {
            return false;
        }

        if (amount > state.availableBalance) {
            return false;
        }

        if (selectedPlanData.minStake && amount < selectedPlanData.minStake) {
            return false;
        }

        if (selectedPlanData.maxStake && amount > selectedPlanData.maxStake) {
            return false;
        }

        return true;
    }, [selectedPlanData, state.stakeAmount, state.availableBalance, state.coinData, userData.email]);

    const handleStakeNow = () => {
        // First validate the amount and update error message if needed
        validateStakeAmount();

        // Then check if the amount is valid using our memoized value
        if (selectedPlanData && state.coinData && isValidStakeAmount) {
            // Update multiple state properties at once
            updateState({
                agreeToTerms: false, // Reset checkbox
                showConfirmation: true
            });
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }
    };

    const handleCloseModal = () => {
        updateState({ showConfirmation: false });
        // Restore body scroll when modal is closed
        document.body.style.overflow = 'unset';
    };

    const handleConfirmStake = () => {
        if (state.agreeToTerms && selectedPlanData) {
            // Get the periodId from the selected plan
            const stakingPeriodId = selectedPlanData.periodId;
            const amount = parseFloat(state.stakeAmount);

            if (!stakingPeriodId) {
                console.error('Missing period ID for selected plan');
                handleCloseModal();
                return;
            }

            // Set loading state
            updateState({ isRedirecting: true });

            // Call the API to create a staking subscription
            _createStakingSubscription(
                {
                    stakingPeriodId,
                    amount,
                    stakingProviderId: selectedPlanData.providerId || 1 // Use provider_id from plan data or default to 1
                },
                (response) => {
                    console.log('Staking subscription created:', response);

                    // Show success toast
                    showSuccessSnackbar({
                        alertMessage: 'Staking subscription created successfully!',
                        actionType: 'link',
                        buttonMessage: 'View Portfolio',
                        linkTo: '/account/staking/portfolio'
                    });

                    // Wait 3 seconds before redirecting to allow user to see success message
                    setTimeout(() => {
                        handleCloseModal();
                        updateState({
                            stakeAmount: '',
                            isRedirecting: false
                        }); // Reset form
                        navigate('/account/staking/portfolio');
                    }, 3000);
                },
                (error) => {
                    console.error('Error creating staking subscription:', error);

                    handleCloseModal();
                    updateState({ isRedirecting: false });

                    // Show error toast
                    showErrorSnackbar({
                        alertMessage: 'Failed to create staking subscription. Please try again.'
                    });
                }
            );
        }
    };

    const calculateRewards = useCallback((amount, apr, days) => {
        if (!amount || isNaN(parseFloat(amount))) return 0;
        const principal = parseFloat(amount);
        const annualRate = (apr || 0) / 100;
        const dailyRate = annualRate / 365;
        return principal * dailyRate * days;
    }, []);

    // Show shimmer loading UI that matches the layout when loading or no data yet
    // IMPORTANT: We must check stakingLoading first before checking state.coinData to avoid infinite loop
    if (stakingLoading || !state.coinData) {
        console.log("Rendering shimmer loading UI");
        return (
            <div className={Styles.stakingContainer}>
                {/* Header with shimmer */}
                <div className={Styles.headerSection}>
                    <div className={Styles.headerContent}>
                        <div className={Styles.coinInfo}>
                            <div className={`${Styles.coinIcon} ${Styles.shimmer}`} style={{ width: '48px', height: '48px', borderRadius: '50%' }}></div>
                            <div className={Styles.coinDetails}>
                                <div className={`${Styles.shimmer}`} style={{ width: '180px', height: '32px', marginBottom: '8px' }}></div>
                                <div className={Styles.coinMeta}>
                                    <div className={`${Styles.shimmer}`} style={{ width: '60px', height: '24px', marginRight: '12px' }}></div>
                                    <div className={`${Styles.shimmer}`} style={{ width: '120px', height: '24px' }}></div>
                                </div>
                            </div>
                        </div>
                        <div className={`${Styles.shimmer}`} style={{ width: '120px', height: '40px', borderRadius: '20px' }}></div>
                    </div>
                </div>

                <div className={Styles.mainContent}>
                    {/* Left Column */}
                    <div className={Styles.leftColumn}>
                        {/* Staking Plans Section */}
                        <div className={Styles.plansSection}>
                            <div className={`${Styles.shimmer}`} style={{ width: '220px', height: '28px', marginBottom: '24px' }}></div>

                            <div className={Styles.plansGrid}>
                                {/* Generate 4 shimmer plan cards */}
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className={`${Styles.planCard}`}>
                                        <div className={Styles.planHeader}>
                                            <div className={`${Styles.shimmer}`} style={{ width: '80px', height: '24px' }}></div>
                                            <div className={`${Styles.shimmer}`} style={{ width: '70px', height: '24px' }}></div>
                                        </div>
                                        <div className={Styles.planDetails}>
                                            {[1, 2, 3].map((j) => (
                                                <div key={j} className={Styles.planDetailRow}>
                                                    <div className={`${Styles.shimmer}`} style={{ width: '60px', height: '18px' }}></div>
                                                    <div className={`${Styles.shimmer}`} style={{ width: '80px', height: '18px' }}></div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className={`${Styles.shimmer}`} style={{ width: '100%', height: '80px', marginTop: '16px' }}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className={Styles.rightColumn}>
                        <div className={Styles.stakingForm}>
                            <div className={`${Styles.shimmer}`} style={{ width: '150px', height: '28px', marginBottom: '24px' }}></div>

                            {/* Plan Summary */}
                            <div className={Styles.planSummary}>
                                <div className={Styles.planSummaryHeader}>
                                    <div className={`${Styles.shimmer}`} style={{ width: '100px', height: '20px' }}></div>
                                    <div className={`${Styles.shimmer}`} style={{ width: '80px', height: '20px' }}></div>
                                </div>
                                <div className={`${Styles.shimmer}`} style={{ width: '70px', height: '24px', marginTop: '8px' }}></div>
                            </div>

                            {/* Balance Card */}
                            <div className={Styles.balanceCard}>
                                <div className={Styles.balanceRow}>
                                    <div className={`${Styles.shimmer}`} style={{ width: '120px', height: '20px' }}></div>
                                    <div className={`${Styles.shimmer}`} style={{ width: '100px', height: '20px' }}></div>
                                </div>
                            </div>

                            {/* Input Group */}
                            <div className={Styles.inputGroup}>
                                <div className={`${Styles.shimmer}`} style={{ width: '150px', height: '20px', marginBottom: '8px' }}></div>
                                <div className={`${Styles.shimmer}`} style={{ width: '100%', height: '48px', borderRadius: '8px' }}></div>
                            </div>

                            {/* Limits */}
                            <div className={Styles.stakingLimits}>
                                <div className={`${Styles.shimmer}`} style={{ width: '100px', height: '18px', marginRight: '16px' }}></div>
                                <div className={`${Styles.shimmer}`} style={{ width: '100px', height: '18px' }}></div>
                            </div>

                            {/* Rewards Estimate */}
                            <div className={Styles.rewardsEstimate}>
                                <div className={`${Styles.shimmer}`} style={{ width: '150px', height: '20px', marginBottom: '16px' }}></div>
                                <div className={`${Styles.shimmer}`} style={{ width: '100%', height: '80px', borderRadius: '8px' }}></div>
                            </div>

                            {/* Button */}
                            <div className={`${Styles.shimmer}`} style={{ width: '100%', height: '48px', borderRadius: '24px', marginTop: '24px' }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Only log on first render or when debugging is needed
    // Remove this console.log in production as it can cause performance issues
    /*
    console.log("About to render with state:", {
        coinData: state.coinData,
        stakingPlans: state.stakingPlans?.length,
        selectedPlan: state.selectedPlan
    });
    */

    // If coinData is still null, show the shimmer loading UI
    if (!state.coinData) {
        return (
            <div className={Styles.stakingContainer}>
                <div className={Styles.headerSection}>
                    <div className={Styles.headerContent}>
                        <div className={Styles.coinInfo}>
                            <div className={`${Styles.coinIcon} ${Styles.shimmer}`} style={{ width: '48px', height: '48px', borderRadius: '50%' }}></div>
                            <div className={Styles.coinDetails}>
                                <div className={`${Styles.shimmer}`} style={{ width: '180px', height: '32px', marginBottom: '8px' }}></div>
                                <div className={Styles.coinMeta}>
                                    <div className={`${Styles.shimmer}`} style={{ width: '60px', height: '24px', marginRight: '12px' }}></div>
                                    <div className={`${Styles.shimmer}`} style={{ width: '120px', height: '24px' }}></div>
                                </div>
                            </div>
                        </div>
                        <div className={`${Styles.shimmer}`} style={{ width: '120px', height: '40px', borderRadius: '20px' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={Styles.stakingContainer}>
            {/* Header */}
            <div className={Styles.headerSection}>
                <div className={Styles.headerContent}>
                    <div className={Styles.coinInfo}>
                        <div
                            className={Styles.coinIcon}
                            style={state.coinData.iconUrl ? {
                                backgroundImage: `url(${state.coinData.iconUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: 'transparent'
                            } : { backgroundColor: state.coinData.color }}
                        >
                            {!state.coinData.iconUrl && getDefaultIcon(state.coinData.asset)}
                        </div>

                        <div className={Styles.coinDetails}>
                            <h1 className={Styles.coinTitle}>
                                {getCoinName(state.coinData.asset)} Staking
                            </h1>
                            <div className={Styles.coinMeta}>
                                <span className={Styles.coinSymbol}>
                                    {String(state.coinData.asset || 'N/A')}
                                </span>
                                <span className={Styles.coinAPY}>
                                    Earn up to {Math.max(...state.stakingPlans.map(plan => plan.apr))}% APR
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={Styles.headerButtons}>
                        <Button
                            text="Back to Pools"
                            onClick={() => navigate('/staking')}
                            className={Styles.backButton}
                            fitContent={true}
                        />

                        <Button
                            text="Advanced Calculator"
                            onClick={() => navigate('/staking/calculator')}
                            className={Styles.calculatorButton}
                            fitContent={true}
                        />
                    </div>
                </div>
            </div>

            <div className={Styles.mainContent}>
                {/* Left Column */}
                <div className={Styles.leftColumn}>
                    {/* Staking Plans */}
                    <div className={Styles.plansSection}>
                        <h3 className={Styles.sectionTitle}>
                            Choose Your Staking Plan
                        </h3>

                        <div className={Styles.plansGrid}>
                            {state.stakingPlans.map((plan) => (
                                <div
                                    key={plan.id}
                                    onClick={() => !plan.soldOut && updateState({ selectedPlan: plan.id })}
                                    className={`${Styles.planCard} 
                                        ${state.selectedPlan === plan.id ? Styles.planCardSelected : ''} 
                                        ${plan.soldOut ? Styles.planCardSoldOut : ''}`}
                                >
                                    {plan.popular && !plan.soldOut && (
                                        <div className={Styles.popularBadge}>
                                            POPULAR
                                        </div>
                                    )}
                                    {plan.soldOut && (
                                        <div className={Styles.soldOutBadge}>
                                            SOLD OUT
                                        </div>
                                    )}

                                    <div className={Styles.planHeader}>
                                        <div className={Styles.planDuration}>
                                            {String(plan.duration || 'N/A')}
                                        </div>
                                        <div className={Styles.planAPY}>
                                            {String(plan.apr || 0)}% APR
                                        </div>
                                    </div>

                                    <div className={Styles.planDetails}>
                                        <div className={Styles.planDetailRow}>
                                            <span className={Styles.planDetailLabel}>Type:</span>
                                            <span className={Styles.planDetailValue}>
                                                {String(plan.type || 'N/A')}
                                            </span>
                                        </div>
                                        <div className={Styles.planDetailRow}>
                                            <span className={Styles.planDetailLabel}>Rewards:</span>
                                            <span className={Styles.planDetailValue}>
                                                {String(plan.rewards || 'N/A')}
                                            </span>
                                        </div>
                                        <div className={Styles.planDetailRow}>
                                            <span className={Styles.planDetailLabel}>Risk:</span>
                                            <span
                                                className={Styles.planDetailValue}
                                                style={{ color: getRiskColor(plan.risk) }}
                                            >
                                                {String(plan.risk || 'N/A')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Plan Features */}
                    {selectedPlanData && (
                        <div className={Styles.featuresSection}>
                            <h3 className={Styles.sectionTitle}>
                                Plan Features
                            </h3>

                            <div className={Styles.featuresGrid}>
                                {selectedPlanData.features && selectedPlanData.features.length > 0 ? (
                                    selectedPlanData.features.map((feature, index) => {
                                        const featureIcon = getFeatureIcon(feature);
                                        return (
                                            <div key={index} className={Styles.featureItem}>
                                                <div className={Styles.featureIcon}>
                                                    {featureIcon}
                                                </div>
                                                <span className={Styles.featureText}>{String(feature?.name || feature || 'Feature')}</span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className={Styles.noFeatures}>
                                        <FiInfo className={Styles.noFeaturesIcon} />
                                        <span className={Styles.noFeaturesText}>
                                            This staking period doesn't include additional features
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column */}
                <div className={Styles.rightColumn}>
                    <div className={Styles.stakingForm}>
                        <h3 className={Styles.formTitle}>
                            Stake {getCoinName(state.coinData.asset)}
                        </h3>

                        {/* Warning for non-stakable coins */}
                        {userData.email && !state.stakable && (
                            <div className={Styles.stakableWarning}>
                                <FiAlertTriangle className={Styles.warningIcon} />
                                <span>This coin is currently not available for staking in your account.</span>
                            </div>
                        )}

                        {/* Selected Plan Summary */}
                        {selectedPlanData && (
                            <div className={Styles.planSummary}>
                                <div className={Styles.planSummaryHeader}>
                                    <span className={Styles.planSummaryLabel}>Selected Plan</span>
                                    <span className={Styles.planSummaryValue}>
                                        {selectedPlanData?.duration || 'N/A'}
                                    </span>
                                </div>
                                <div className={Styles.planSummaryAPY}>
                                    {selectedPlanData?.apr || 0}% APR
                                </div>
                            </div>
                        )}

                        {/* Available Balance */}
                        <div className={Styles.balanceCard}>
                            <div className={Styles.balanceRow}>
                                <span className={Styles.balanceLabel}>Available Balance</span>
                                <span className={Styles.balanceValue}>
                                    {userData.email ? (
                                        `${formatNumber(state.availableBalance, 8)} ${state.coinData.asset}`
                                    ) : (
                                        "Log In First"
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Stake Amount Input */}
                        <div className={Styles.inputGroup}>
                            <label className={Styles.inputLabel}>
                                Stake Amount ({state.coinData.asset})
                            </label>
                            <div className={Styles.inputWrapper}>
                                <input
                                    type="number"
                                    value={state.stakeAmount}
                                    onChange={(e) => {
                                        // Update stakeAmount and clear error in a single update
                                        updateState({
                                            stakeAmount: e.target.value,
                                            errorMessage: '' // Clear error when user starts typing
                                        });
                                    }}
                                    onBlur={() => {
                                        // Validate when user finishes typing
                                        if (state.stakeAmount && selectedPlanData && state.coinData) validateStakeAmount();
                                    }}
                                    placeholder="0.00"
                                    className={`${Styles.amountInput} ${state.errorMessage ? Styles.inputError : ''}`}
                                />
                                <div className={Styles.inputActions}>
                                    {userData.email && state.availableBalance > 0 && (
                                        <button
                                            type="button"
                                            className={Styles.maxButton}
                                            onClick={() => {
                                                if (selectedPlanData) {
                                                    const maxAmount = Math.min(state.availableBalance, selectedPlanData.maxStake || state.availableBalance);
                                                    // Update stakeAmount and clear error in a single update
                                                    updateState({
                                                        stakeAmount: maxAmount.toString(),
                                                        errorMessage: '' // Clear any error message when using MAX
                                                    });
                                                }
                                            }}
                                        >
                                            MAX
                                        </button>
                                    )}
                                    <span className={Styles.inputSuffix}>
                                        {String(state.coinData.asset || 'N/A')}
                                    </span>
                                </div>
                                {state.errorMessage && (
                                    <div className={Styles.errorMessage}>
                                        {state.errorMessage}
                                    </div>
                                )}
                            </div>
                            <div className={Styles.inputLimits}>
                                <span>Min: {selectedPlanData?.minStake || 0} {state.coinData.asset}</span>
                                <span>Max: {selectedPlanData?.maxStake || 0} {state.coinData.asset}</span>
                            </div>
                            {state.stakeAmount && parseFloat(state.stakeAmount) > state.availableBalance && (
                                <div className={Styles.errorMessage}>
                                    Insufficient balance. Available: {formatNumber(state.availableBalance, 8)} {state.coinData.asset}
                                </div>
                            )}
                        </div>

                        {/* Earnings Preview */}
                        {state.stakeAmount && selectedPlanData && (
                            <div className={Styles.earningsPreview}>
                                <h4 className={Styles.earningsTitle}>
                                    Estimated Earnings
                                    {state.earningsLoading && <span className={Styles.loadingIndicator}> (Loading...)</span>}
                                </h4>

                                <div className={Styles.earningsList}>
                                    <div className={Styles.earningsRow}>
                                        <span className={Styles.earningsLabel}>Daily:</span>
                                        <span className={Styles.earningsValue}>
                                            {state.earningsLoading ? (
                                                "Loading..."
                                            ) : state.estimatedEarnings ? (
                                                `${parseFloat(state.estimatedEarnings.daily_reward).toFixed(6)} ${state.coinData.asset}`
                                            ) : (
                                                "Calculating..."
                                            )}
                                        </span>
                                    </div>
                                    <div className={Styles.earningsRow}>
                                        <span className={Styles.earningsLabel}>Monthly:</span>
                                        <span className={Styles.earningsValue}>
                                            {state.earningsLoading ? (
                                                "Loading..."
                                            ) : state.estimatedEarnings ? (
                                                `${(parseFloat(state.estimatedEarnings.daily_reward) * 30).toFixed(6)} ${state.coinData.asset}`
                                            ) : (
                                                "Calculating..."
                                            )}
                                        </span>
                                    </div>
                                    <div className={Styles.earningsRow}>
                                        <span className={Styles.earningsLabel}>
                                            {selectedPlanData && selectedPlanData.type === 'flexible' ? 'Yearly Return:' : 'Total Return:'}
                                        </span>
                                        <span className={Styles.earningsValue}>
                                            {state.earningsLoading ? (
                                                "Loading..."
                                            ) : state.estimatedEarnings ? (
                                                selectedPlanData && selectedPlanData.type === 'flexible' ?
                                                    `${parseFloat(state.estimatedEarnings.yearly_reward).toFixed(6)} ${state.coinData.asset}` :
                                                    `${parseFloat(state.estimatedEarnings.duration_reward).toFixed(6)} ${state.coinData.asset}`
                                            ) : (
                                                "Calculating..."
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className={Styles.actionButtons}>
                            <Button
                                text="Stake Now"
                                onClick={handleStakeNow}
                                disabled={!isValidStakeAmount}
                                className={Styles.stakeButton}
                            />
                        </div>

                        {/* Important Notice */}
                        <div className={Styles.importantNotice}>
                            <FiAlertTriangle className={Styles.warningIcon} />
                            <div className={Styles.warningContent}>
                                <div className={Styles.warningTitle}>
                                    Important Notice
                                </div>
                                <div className={Styles.warningText}>
                                    Staking involves locking your cryptocurrency for a specified period. Please review all terms and conditions before proceeding.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {state.showConfirmation && (
                <div className={Styles.modalOverlay}>
                    <div className={Styles.modalContent}>
                        {/* Modal Header */}
                        <div className={Styles.modalHeader}>
                            <h3 className={Styles.modalTitle}>
                                Confirm Staking Transaction
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className={Styles.modalClose}
                            >
                                <FiX />
                            </button>
                        </div>

                        {/* Transaction Summary */}
                        <div className={Styles.transactionSummary}>
                            <h4 className={Styles.summaryTitle}>
                                Transaction Summary
                            </h4>

                            <div className={Styles.summaryList}>
                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Stake Amount:</span>
                                    <span className={Styles.summaryValue}>
                                        {state.stakeAmount} {String(state.coinData.asset || 'N/A')}
                                    </span>
                                </div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Selected Plan:</span>
                                    <span className={Styles.summaryValue}>
                                        {selectedPlanData?.duration}
                                    </span>
                                </div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Annual Percentage Rate:</span>
                                    <span className={Styles.summaryValue}>
                                        {selectedPlanData?.apr}% APR
                                    </span>
                                </div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Reward Frequency:</span>
                                    <span className={Styles.summaryValue}>
                                        {selectedPlanData?.rewards}
                                    </span>
                                </div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Risk Level:</span>
                                    <span
                                        className={Styles.summaryValue}
                                        style={{ color: getRiskColor(selectedPlanData?.risk) }}
                                    >
                                        {selectedPlanData?.risk}
                                    </span>
                                </div>

                                <div className={Styles.summaryDivider}></div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>Estimated Daily Earnings:</span>
                                    <span className={Styles.summaryValue}>
                                        {state.earningsLoading ? (
                                            "Loading..."
                                        ) : state.estimatedEarnings ? (
                                            `${parseFloat(state.estimatedEarnings.daily_reward).toFixed(6)} ${state.coinData.asset}`
                                        ) : (
                                            "Calculating..."
                                        )}
                                    </span>
                                </div>

                                <div className={Styles.summaryRow}>
                                    <span className={Styles.summaryLabel}>
                                        {selectedPlanData && selectedPlanData.type === 'flexible' ? 'Estimated Yearly Return:' : 'Total Estimated Return:'}
                                    </span>
                                    <span className={Styles.summaryValue}>
                                        {state.earningsLoading ? (
                                            "Loading..."
                                        ) : state.estimatedEarnings ? (
                                            selectedPlanData && selectedPlanData.type === 'flexible' ?
                                                `${parseFloat(state.estimatedEarnings.yearly_reward).toFixed(6)} ${state.coinData.asset}` :
                                                `${parseFloat(state.estimatedEarnings.duration_reward).toFixed(6)} ${state.coinData.asset}`
                                        ) : (
                                            "Calculating..."
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Plan Features */}
                        <div className={Styles.modalFeatures}>
                            <h4 className={Styles.featuresTitle}>
                                Plan Features
                            </h4>

                            <div className={Styles.modalFeaturesList}>
                                {selectedPlanData?.features && selectedPlanData.features.length > 0 ? (
                                    selectedPlanData.features.map((feature, index) => {
                                        const featureIcon = getFeatureIcon(feature);
                                        return (
                                            <div key={index} className={Styles.modalFeatureItem}>
                                                <div className={Styles.modalFeatureIcon}>
                                                    {featureIcon}
                                                </div>
                                                <span className={Styles.modalFeatureText}>{String(feature?.name || feature || 'Feature')}</span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className={Styles.modalNoFeatures}>
                                        <FiInfo className={Styles.modalNoFeaturesIcon} />
                                        <span className={Styles.modalNoFeaturesText}>
                                            This staking period doesn't include additional features
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className={Styles.termsSection}>
                            <h4 className={Styles.termsTitle}>
                                Important Information
                            </h4>

                            <div className={Styles.termsList}>
                                <p>• Staking involves locking your cryptocurrency for the specified period</p>
                                <p>• Early withdrawal may not be possible depending on the plan selected</p>
                                <p>• Rewards are estimates and may vary based on network conditions</p>
                                <p>• You acknowledge understanding of the risks involved in cryptocurrency staking</p>
                                <p>• By proceeding, you agree to our Terms of Service and Privacy Policy</p>
                            </div>
                        </div>

                        {/* Agreement Checkbox */}
                        <div className={Styles.agreementSection}>
                            <input
                                type="checkbox"
                                id="agreeTerms"
                                checked={state.agreeToTerms}
                                onChange={(e) => updateState({ agreeToTerms: e.target.checked })}
                                className={Styles.agreementCheckbox}
                            />
                            <label htmlFor="agreeTerms" className={Styles.agreementLabel}>
                                I have read and agree to the terms and conditions. I understand that staking involves risks and that my cryptocurrency will be locked for the specified period. I acknowledge that rewards are estimates and may vary.
                            </label>
                        </div>

                        {/* Action Buttons */}
                        <div className={Styles.modalActions}>
                            <Button
                                text="Cancel"
                                onClick={handleCloseModal}
                                className={Styles.cancelButton}
                            />

                            <Button
                                text={state.isRedirecting ? "Redirecting..." : "Confirm Stake"}
                                onClick={handleConfirmStake}
                                disabled={!state.agreeToTerms || state.isRedirecting}
                                className={Styles.confirmButton}
                                isLoading={state.isRedirecting}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StakingSingleCoinPage;